import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { OptionKey, AttemptAnswer } from '../../types';
import {
  Timer,
  Flag,
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  RotateCcw,
  CheckCircle2,
  Check,
  HelpCircle,
  Clock,
  X,
} from 'lucide-react';
import { useToast } from '../ui/Toast';

export const QuizTestView: React.FC = () => {
  const {
    currentUser,
    activeQuestionSet,
    questions,
    subjects,
    setCurrentView,
    fetchAttempts,
    questionLanguage,
    setQuestionLanguage,
  } = useApp();
  const { showToast } = useToast();

  if (!activeQuestionSet) {
    return (
      <div className="text-center py-20">
        <p className="text-[#5F6368] mb-4">No question set active.</p>
        <button
          onClick={() => setCurrentView('student-dashboard')}
          className="px-5 py-2.5 bg-[#56E8E1] text-[#111111] hover:bg-[#34d5cd] rounded-xl text-xs font-bold transition-all cursor-pointer"
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  const questionsList = Array.isArray(questions) ? questions : [];
  const setQuestions = questionsList.filter(
    (q) => q.question_set_id === activeQuestionSet.id && q.status === 'active'
  );

  const subject = subjects.find((s) => s.id === activeQuestionSet.subject_id);
  const totalMinutes = activeQuestionSet.time_limit_minutes || 10;
  const initialSeconds = totalMinutes * 60;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, OptionKey>>({});
  const [markedForReview, setMarkedForReview] = useState<Record<number, boolean>>({});
  const [timeLeft, setTimeLeft] = useState(initialSeconds);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [startTime] = useState(Date.now());

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) {
      handleSubmitTest();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const currentQ = setQuestions[currentIndex];

  const handleSelectOption = (opt: OptionKey) => {
    if (!currentQ) return;
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQ.id]: opt,
    }));
  };

  const handleClearOption = () => {
    if (!currentQ) return;
    setSelectedAnswers((prev) => {
      const copy = { ...prev };
      delete copy[currentQ.id];
      return copy;
    });
    showToast('Selection cleared', 'info');
  };

  const handleToggleMark = () => {
    if (!currentQ) return;
    setMarkedForReview((prev) => {
      const nextVal = !prev[currentQ.id];
      showToast(nextVal ? 'Question marked for review' : 'Removed review flag', 'info');
      return {
        ...prev,
        [currentQ.id]: nextVal,
      };
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitTest = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    let correctCount = 0;
    let wrongCount = 0;
    let skippedCount = 0;

    const answersList: AttemptAnswer[] = setQuestions.map((q) => {
      const chosen = selectedAnswers[q.id] || null;
      if (!chosen) {
        skippedCount++;
        return {
          question_id: q.id,
          selected_option: null,
          correct_option: q.correct_option,
          is_correct: false,
          is_marked_for_review: !!markedForReview[q.id],
        };
      }

      const isMatch = chosen === q.correct_option;
      if (isMatch) correctCount++;
      else wrongCount++;

      return {
        question_id: q.id,
        selected_option: chosen,
        correct_option: q.correct_option,
        is_correct: isMatch,
        is_marked_for_review: !!markedForReview[q.id],
      };
    });

    const elapsedSeconds = Math.max(1, Math.round((Date.now() - startTime) / 1000));
    const scorePct = setQuestions.length > 0 ? Math.round((correctCount / setQuestions.length) * 100) : 0;

    try {
      console.log('[Test Submission Step 1] Creating attempt via /api/attempts.php?action=create');
      const formData = new URLSearchParams();
      formData.append('question_set_id', String(activeQuestionSet.id));
      formData.append('mode', 'test');

      const startRes = await fetch('/api/attempts.php?action=create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
        body: formData.toString(),
      });
      const startData = await startRes.json().catch((err) => {
        console.error('JSON parse error on attempts.php response:', err);
        return null;
      });

      console.log('[Test Submission Step 1 Response]', startRes.status, startData);

      if (!startData?.success || !startData?.attempt_id) {
        throw new Error(startData?.error || `Failed to create attempt (HTTP ${startRes.status})`);
      }

      const serverAttemptId = startData.attempt_id;

      console.log('[Test Submission Step 2] Submitting answers via /api/submit_test.php for attempt ID:', serverAttemptId);
      const submitRes = await fetch('/api/submit_test.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          attempt_id: serverAttemptId,
          time_spent: elapsedSeconds,
          answers: selectedAnswers,
        }),
      });
      const submitData = await submitRes.json().catch((err) => {
        console.error('JSON parse error on submit_test.php response:', err);
        return null;
      });

      console.log('[Test Submission Step 2 Response]', submitRes.status, submitData);

      if (!submitRes.ok || !submitData?.success) {
        throw new Error(submitData?.error || `Failed to submit test answers (HTTP ${submitRes.status})`);
      }

      console.log('[Test Submission Step 3] Refreshing attempts via fetchAttempts()');
      await fetchAttempts();

      console.log('[Test Submission Step 4] Success - Navigating to ScorecardView');
      showToast(`Exam submitted! Score: ${scorePct}%`, 'success', 'Exam Completed');
      setIsSubmitModalOpen(false);
      setCurrentView('quiz-scorecard');
    } catch (e: any) {
      console.error('Test submission error caught:', e);
      const errorMsg = typeof e === 'string' ? e : e?.message ? String(e.message) : 'Error submitting exam';
      showToast(errorMsg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const minutesLeft = Math.floor(timeLeft / 60);
  const secondsLeft = timeLeft % 60;
  const isTimeCritical = timeLeft <= 120; // under 2 minutes
  const isTimeWarning = timeLeft <= 300 && timeLeft > 120; // under 5 minutes

  const answeredCount = Object.keys(selectedAnswers).length;
  const markedCount = Object.values(markedForReview).filter(Boolean).length;
  const unansweredCount = setQuestions.length - answeredCount;

  const currentSelectedOpt = currentQ ? selectedAnswers[currentQ.id] : null;
  const isCurrentMarked = currentQ ? !!markedForReview[currentQ.id] : false;

  // Content resolution with Hindi fallback to English
  const getQuestionText = () => {
    if (!currentQ) return '';
    if (questionLanguage === 'hi' && currentQ.question_text_hi?.trim()) {
      return currentQ.question_text_hi;
    }
    return currentQ.question_text;
  };

  const getOptionText = (key: OptionKey) => {
    if (!currentQ) return '';
    if (questionLanguage === 'hi') {
      if (key === 'A' && currentQ.option_a_hi?.trim()) return currentQ.option_a_hi;
      if (key === 'B' && currentQ.option_b_hi?.trim()) return currentQ.option_b_hi;
      if (key === 'C' && currentQ.option_c_hi?.trim()) return currentQ.option_c_hi;
      if (key === 'D' && currentQ.option_d_hi?.trim()) return currentQ.option_d_hi;
    }
    if (key === 'A') return currentQ.option_a;
    if (key === 'B') return currentQ.option_b;
    if (key === 'C') return currentQ.option_c;
    return currentQ.option_d;
  };

  const optionsList: { key: OptionKey; text: string }[] = currentQ
    ? [
        { key: 'A', text: getOptionText('A') },
        { key: 'B', text: getOptionText('B') },
        { key: 'C', text: getOptionText('C') },
        { key: 'D', text: getOptionText('D') },
      ]
    : [];

  // Timer color classes logic
  let timerBoxStyle = 'bg-[#F7FAFA] border-[#E5E9E9] text-[#111111]';
  let timerClockStyle = 'text-[#1b8e88]';
  let timerLabelStyle = 'text-[#5F6368]';

  if (isTimeCritical) {
    timerBoxStyle = 'bg-[#FDEDEF] border-[#D94A5C] text-[#D94A5C] animate-pulse ring-2 ring-[#D94A5C]/20';
    timerClockStyle = 'text-[#D94A5C]';
    timerLabelStyle = 'text-[#D94A5C]';
  } else if (isTimeWarning) {
    timerBoxStyle = 'bg-[#FFF7D9] border-[#EBC138] text-[#8a6508]';
    timerClockStyle = 'text-[#8a6508]';
    timerLabelStyle = 'text-[#8a6508]';
  }

  return (
    <div className="max-w-6xl mx-auto space-y-5 pb-24 md:pb-16">
      {/* 1. Exam Top Bar */}
      <div className="bg-white rounded-2xl border border-[#E5E9E9] p-4 sm:p-5 shadow-[0_4px_20px_rgba(17,17,17,0.03)] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/40 flex items-center gap-1">
              <Timer className="w-3 h-3 text-[#1fb4ac]" />
              Timed Exam Mode
            </span>
            {subject && (
              <span className="text-xs text-[#5F6368] font-medium">
                • {subject.name}
              </span>
            )}
          </div>
          <h1 className="text-base sm:text-lg font-bold text-[#111111] truncate">
            {activeQuestionSet.name}
          </h1>
        </div>

        {/* Real-time Timer Box */}
        <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
          <div
            className={`flex items-center gap-2.5 px-4 py-2 sm:py-2.5 rounded-2xl font-mono border-2 shadow-xs transition-all ${timerBoxStyle}`}
          >
            <Clock className={`w-5 h-5 shrink-0 ${timerClockStyle}`} />
            <div className="flex flex-col items-start leading-none">
              <span className={`text-[9px] font-sans font-extrabold uppercase tracking-wider mb-0.5 ${timerLabelStyle}`}>
                Time Left
              </span>
              <span className="text-base sm:text-lg font-black tracking-tight">
                {String(minutesLeft).padStart(2, '0')}:{String(secondsLeft).padStart(2, '0')}
              </span>
            </div>
          </div>

          <button
            onClick={() => setIsSubmitModalOpen(true)}
            className="px-4 sm:px-5 py-2.5 sm:py-3 rounded-2xl bg-[#20A66A] hover:bg-[#1a8e5a] text-white font-extrabold text-xs sm:text-sm shadow-sm transition-all cursor-pointer whitespace-nowrap"
          >
            Submit Exam
          </button>
        </div>
      </div>

      {/* 2. Main Question Grid & Question Palette Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Left 2 Cols: Question Area */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-3xl border border-[#E5E9E9] shadow-[0_4px_20px_rgba(17,17,17,0.03)] overflow-hidden">
            <div>
              {/* Question Header */}
              <div className="p-4 sm:p-5 border-b border-[#E5E9E9] flex items-center justify-between bg-[#F7FAFA]">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-xl bg-[#FFF7D9] text-[#8a6508] font-extrabold text-sm flex items-center justify-center border border-[#EBC138]/40">
                    {currentIndex + 1}
                  </span>
                  <span className="text-xs font-semibold text-[#5F6368]">
                    Question {currentIndex + 1} of {setQuestions.length}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {/* English | हिन्दी Language Segmented Control */}
                  <div className="flex items-center bg-[#F0F4F4] p-0.5 rounded-lg border border-[#E5E9E9]">
                    <button
                      type="button"
                      onClick={() => setQuestionLanguage('en')}
                      className={`px-2.5 py-1 rounded-md text-xs font-extrabold transition-all cursor-pointer ${
                        questionLanguage === 'en'
                          ? 'bg-[#111111] text-white shadow-xs'
                          : 'text-[#5F6368] hover:text-[#111111]'
                      }`}
                    >
                      English
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuestionLanguage('hi')}
                      className={`px-2.5 py-1 rounded-md text-xs font-extrabold transition-all cursor-pointer ${
                        questionLanguage === 'hi'
                          ? 'bg-[#111111] text-white shadow-xs'
                          : 'text-[#5F6368] hover:text-[#111111]'
                      }`}
                    >
                      हिन्दी
                    </button>
                  </div>

                  <button
                    onClick={handleToggleMark}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border flex items-center gap-1.5 transition-all cursor-pointer ${
                      isCurrentMarked
                        ? 'bg-[#FFF7D9] border-[#EBC138] text-[#8a6508]'
                        : 'border-[#E5E9E9] bg-white text-[#5F6368] hover:bg-[#F0F4F4]'
                    }`}
                  >
                    <Flag className={`w-3.5 h-3.5 ${isCurrentMarked ? 'fill-[#EBC138] text-[#8a6508]' : ''}`} />
                    <span>{isCurrentMarked ? 'Marked' : 'Mark for Review'}</span>
                  </button>

                  {currentSelectedOpt && (
                    <button
                      onClick={handleClearOption}
                      className="p-1.5 rounded-xl text-[#5F6368] hover:text-[#111111] hover:bg-[#F0F4F4] transition-colors cursor-pointer"
                      title="Clear selected option"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Question Content */}
              <div className="p-5 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold text-[#111111] leading-relaxed mb-4">
                  {getQuestionText()}
                </h2>

                <div className="space-y-2.5">
                  {optionsList.map(({ key, text }) => {
                    const isSelected = currentSelectedOpt === key;
                    return (
                      <button
                        key={key}
                        onClick={() => handleSelectOption(key)}
                        className={`w-full text-left px-3.5 py-2.5 sm:py-3 rounded-xl border-2 transition-all flex items-center gap-3 focus:outline-none cursor-pointer ${
                          isSelected
                            ? 'border-[#56E8E1] bg-[#E8FAF9] text-[#111111] font-semibold shadow-2xs'
                            : 'border-[#E5E9E9] bg-white hover:border-[#56E8E1]/60 hover:bg-[#E8FAF9]/20 text-[#111111]'
                        }`}
                      >
                        <span
                          className={`w-6 h-6 sm:w-6.5 sm:h-6.5 rounded-lg text-xs font-bold flex items-center justify-center shrink-0 border transition-colors ${
                            isSelected
                              ? 'bg-[#56E8E1] text-[#111111] border-[#56E8E1]'
                              : 'bg-[#F0F4F4] text-[#5F6368] border-[#E5E9E9]'
                          }`}
                        >
                          {isSelected ? <Check className="w-3.5 h-3.5 text-[#111111]" /> : key}
                        </span>
                        <span className="text-xs sm:text-sm leading-snug flex-1">
                          {text}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Question Footer Nav */}
            <div className="p-4 sm:p-5 bg-[#F7FAFA] border-t border-[#E5E9E9] flex items-center justify-between gap-3">
              <button
                onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                disabled={currentIndex === 0}
                className="px-4 py-2.5 rounded-xl border border-[#E5E9E9] bg-white text-xs font-bold text-[#111111] hover:bg-[#F0F4F4] disabled:opacity-40 disabled:pointer-events-none transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </button>

              <div className="text-xs text-[#5F6368] font-semibold hidden sm:block">
                {answeredCount} of {setQuestions.length} answered
              </div>

              <button
                onClick={() => {
                  if (currentIndex < setQuestions.length - 1) {
                    setCurrentIndex((prev) => prev + 1);
                  } else {
                    setIsSubmitModalOpen(true);
                  }
                }}
                className="px-5 py-2.5 rounded-xl bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
              >
                {currentIndex < setQuestions.length - 1 ? (
                  <>
                    Next Question
                    <ArrowRight className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    Review & Submit
                    <CheckCircle2 className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right Col: Question Palette Navigation */}
        <div className="space-y-4">
          <div className="bg-white rounded-3xl border border-[#E5E9E9] p-5 shadow-[0_4px_20px_rgba(17,17,17,0.03)] space-y-4">
            <h3 className="text-sm font-bold text-[#111111] uppercase tracking-wider">
              Question Palette
            </h3>

            {/* Status Legend */}
            <div className="grid grid-cols-2 gap-2 text-xs text-[#5F6368] pb-3 border-b border-[#E5E9E9]">
              <div className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-md bg-[#56E8E1] border border-[#34d5cd] shrink-0" />
                <span>Answered ({answeredCount})</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-md bg-[#EBC138] shrink-0" />
                <span>Review ({markedCount})</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-md bg-[#E5E9E9] shrink-0" />
                <span>Unanswered ({unansweredCount})</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-md border-2 border-[#111111] shrink-0" />
                <span>Current</span>
              </div>
            </div>

            {/* Question Buttons Grid */}
            <div className="grid grid-cols-5 sm:grid-cols-6 lg:grid-cols-5 gap-2 max-h-56 overflow-y-auto p-1">
              {setQuestions.map((q, idx) => {
                const isAnswered = selectedAnswers[q.id] !== undefined;
                const isMarked = !!markedForReview[q.id];
                const isCurrent = idx === currentIndex;

                let btnBg = 'bg-[#F0F4F4] text-[#5F6368] hover:bg-[#E8FAF9] hover:text-[#111111]';
                if (isMarked) {
                  btnBg = 'bg-[#EBC138] text-[#111111] font-bold';
                } else if (isAnswered) {
                  btnBg = 'bg-[#56E8E1] text-[#111111] font-bold';
                }

                return (
                  <button
                    key={q.id}
                    onClick={() => setCurrentIndex(idx)}
                    className={`h-9 rounded-xl text-xs flex items-center justify-center transition-all cursor-pointer relative ${btnBg} ${
                      isCurrent ? 'ring-2 ring-offset-2 ring-[#111111]' : ''
                    }`}
                  >
                    {idx + 1}
                    {isMarked && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#8a6508] rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="pt-3 border-t border-[#E5E9E9]">
              <button
                onClick={() => setIsSubmitModalOpen(true)}
                className="w-full py-2.5 rounded-xl bg-[#111111] hover:bg-[#222222] text-white font-bold text-xs transition-colors cursor-pointer shadow-xs"
              >
                Finalize & Submit Exam
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Confirm Submit Modal */}
      {isSubmitModalOpen && (
        <div
          id="confirm-submit-modal"
          className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4"
        >
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-[#E5E9E9] animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-[#E5E9E9] mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#8a6508]" />
                <h3 className="text-base font-bold text-[#111111]">
                  Ready to Submit Exam?
                </h3>
              </div>
              <button
                onClick={() => setIsSubmitModalOpen(false)}
                className="text-[#5F6368] hover:text-[#111111] p-1 rounded-lg hover:bg-[#F0F4F4]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-[#5F6368] mb-5 leading-relaxed">
              Once submitted, your answers will be evaluated and recorded in your permanent analytics log.
            </p>

            {/* Metrics Breakdown */}
            <div className="grid grid-cols-3 gap-2.5 mb-6 text-center">
              <div className="p-3 rounded-2xl bg-[#E8FAF9] border border-[#56E8E1]/40">
                <div className="text-xl font-extrabold text-[#1b8e88]">{answeredCount}</div>
                <div className="text-[10px] font-bold text-[#1b8e88] uppercase tracking-wider mt-0.5">
                  Answered
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-[#FFF7D9] border border-[#EBC138]/40">
                <div className="text-xl font-extrabold text-[#8a6508]">{markedCount}</div>
                <div className="text-[10px] font-bold text-[#8a6508] uppercase tracking-wider mt-0.5">
                  Flagged
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-[#F0F4F4] border border-[#E5E9E9]">
                <div className="text-xl font-extrabold text-[#5F6368]">{unansweredCount}</div>
                <div className="text-[10px] font-bold text-[#5F6368] uppercase tracking-wider mt-0.5">
                  Unanswered
                </div>
              </div>
            </div>

            {unansweredCount > 0 && (
              <div className="mb-6 p-3 rounded-xl bg-[#FFF7D9] border border-[#EBC138]/40 text-xs text-[#8a6508]">
                Notice: You have <strong>{unansweredCount} unanswered questions</strong>. They will be marked as skipped.
              </div>
            )}

            <div className="flex items-center justify-end gap-2.5">
              <button
                onClick={() => setIsSubmitModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-[#5F6368] hover:bg-[#F0F4F4] rounded-xl transition-colors cursor-pointer"
              >
                Continue Answering
              </button>
              <button
                onClick={handleSubmitTest}
                disabled={isSubmitting}
                className="px-5 py-2.5 text-xs font-bold text-white bg-[#20A66A] hover:bg-[#1a8e5a] rounded-xl shadow-xs transition-colors cursor-pointer disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Yes, Submit Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizTestView;
